import { createSignal } from 'solid-js'
import { getActiveWorkspaceSlug, setActiveWorkspaceSlug } from '../api/client'
import * as workspaceApi from '../api/endpoints/workspace.api'
import type {
  CreateWorkspaceRequest,
  InviteToWorkspaceRequest,
  UpdateWorkspaceRequest,
  WorkspaceInvitation,
  WorkspaceMember,
  WorkspaceMemberRole,
  WorkspaceSummary,
  WorkspaceUsageSummary,
  WorkspaceWithMembers,
} from '../api/types/generated'
import { clearBrandCssCache } from '../shared/utils/brandRampCache'
import { authStore } from './auth.store'
import { refreshAudioPreferencesFromWorkspace } from './preferences.store'

const LAST_WORKSPACE_KEY = 'lastWorkspaceSlug'

function isActiveWorkspace(slug: string | null): boolean {
  return getActiveWorkspaceSlug() === slug
}

interface WorkspaceState {
  workspaces: WorkspaceSummary[]
  workspace: WorkspaceWithMembers | null
  invitations: WorkspaceInvitation[]
  usage: WorkspaceUsageSummary | null
  usageError: unknown
  isLoading: boolean
  error: unknown
}

const [workspaceState, setWorkspaceState] = createSignal<WorkspaceState>({
  workspaces: [],
  workspace: null,
  invitations: [],
  usage: null,
  usageError: null,
  isLoading: false,
  error: null,
})

export const workspaceStore = {
  getState: workspaceState,

  get state() {
    return workspaceState()
  },

  get hasWorkspace(): boolean {
    return workspaceState().workspace !== null
  },

  get workspace(): WorkspaceWithMembers | null {
    return workspaceState().workspace
  },

  get members(): WorkspaceMember[] {
    return workspaceState().workspace?.members ?? []
  },

  get currentUserRole(): WorkspaceMemberRole | null {
    const state = workspaceState()
    const user = authStore.state.user
    if (!state.workspace || !user) {
      return null
    }

    const member = state.workspace.members.find((m) => m.hiring_manager_id === user.id)
    return member?.role ?? null
  },

  get isAdmin(): boolean {
    return this.currentUserRole === 'admin'
  },

  get isManager(): boolean {
    return this.currentUserRole === 'manager'
  },

  get canCreateScenarios(): boolean {
    return this.currentUserRole !== 'manager'
  },

  get managers(): WorkspaceMember[] {
    return (workspaceState().workspace?.members ?? []).filter((m) => m.role === 'manager')
  },

  get isLoading(): boolean {
    return workspaceState().isLoading
  },

  get error(): unknown {
    return workspaceState().error
  },

  get usage(): WorkspaceUsageSummary | null {
    const { usage, workspace } = workspaceState()
    return usage && usage.workspace_id === workspace?.id ? usage : null
  },

  get usageError(): unknown {
    return workspaceState().usageError
  },

  get workspaces(): WorkspaceSummary[] {
    return workspaceState().workspaces
  },

  get defaultWorkspaceSlug(): string | null {
    const saved = localStorage.getItem(LAST_WORKSPACE_KEY)
    const list = workspaceState().workspaces
    if (saved && list.some((w) => w.slug === saved)) {
      return saved
    }
    return list[0]?.slug ?? null
  },

  async fetchWorkspaces(): Promise<WorkspaceSummary[]> {
    const workspaces = await workspaceApi.fetchWorkspaces()
    setWorkspaceState((prev) => ({ ...prev, workspaces }))
    return workspaces
  },

  switchWorkspace(slug: string) {
    setActiveWorkspaceSlug(slug)
    localStorage.setItem(LAST_WORKSPACE_KEY, slug)
  },

  setLoading(isLoading: boolean) {
    setWorkspaceState((prev) => ({ ...prev, isLoading, error: null }))
  },

  setError(error: unknown) {
    setWorkspaceState((prev) => ({ ...prev, error, isLoading: false }))
  },

  setWorkspaceData(workspace: WorkspaceWithMembers | null) {
    setWorkspaceState((prev) => ({
      ...prev,
      workspace,
      isLoading: false,
      error: null,
    }))
    refreshAudioPreferencesFromWorkspace(workspace?.audio_preferences)
  },

  async fetchWorkspace(): Promise<void> {
    const requestedSlug = getActiveWorkspaceSlug()
    this.setLoading(true)
    try {
      const workspace = await workspaceApi.fetchWorkspace()
      if (!isActiveWorkspace(requestedSlug)) {
        setWorkspaceState((prev) => ({ ...prev, isLoading: false }))
        return
      }
      this.setWorkspaceData(workspace)
    } catch (error) {
      setWorkspaceState((prev) =>
        isActiveWorkspace(requestedSlug)
          ? { ...prev, workspace: null, isLoading: false, error }
          : { ...prev, isLoading: false }
      )
    }
  },

  async createWorkspace(
    data: CreateWorkspaceRequest,
    options: { callerHandlesError?: boolean } = {}
  ): Promise<WorkspaceWithMembers> {
    this.setLoading(true)
    try {
      const workspace = await workspaceApi.createWorkspace(data)
      setWorkspaceState((prev) => ({
        ...prev,
        workspace,
        isLoading: false,
        error: null,
      }))
      return workspace
    } catch (error) {
      if (options.callerHandlesError) {
        setWorkspaceState((prev) => ({ ...prev, isLoading: false }))
      } else {
        this.setError(error)
      }
      throw error
    }
  },

  async updateWorkspace(
    data: UpdateWorkspaceRequest,
    options: { callerHandlesError?: boolean } = {}
  ): Promise<void> {
    this.setLoading(true)
    try {
      const updated = await workspaceApi.updateWorkspace(data)
      setWorkspaceState((prev) => ({
        ...prev,
        workspace: prev.workspace ? { ...prev.workspace, ...updated } : null,
        isLoading: false,
        error: null,
      }))
    } catch (error) {
      if (options.callerHandlesError) {
        setWorkspaceState((prev) => ({ ...prev, isLoading: false }))
      } else {
        this.setError(error)
      }
      throw error
    }
  },

  async deleteWorkspace(): Promise<void> {
    this.setLoading(true)
    try {
      await workspaceApi.deleteWorkspace()
      setWorkspaceState((prev) => ({
        ...prev,
        workspace: null,
        invitations: [],
        usage: null,
        isLoading: false,
        error: null,
      }))
    } catch (error) {
      this.setError(error)
      throw error
    }
  },

  async leaveWorkspace(): Promise<void> {
    this.setLoading(true)
    try {
      await workspaceApi.leaveWorkspace()
      setWorkspaceState((prev) => ({
        ...prev,
        workspace: null,
        invitations: [],
        usage: null,
        isLoading: false,
        error: null,
      }))
    } catch (error) {
      this.setError(error)
      throw error
    }
  },

  async removeMember(memberId: number): Promise<void> {
    try {
      await workspaceApi.removeMember(memberId)
      setWorkspaceState((prev) => ({
        ...prev,
        workspace: prev.workspace
          ? {
              ...prev.workspace,
              members: prev.workspace.members.filter((m) => m.hiring_manager_id !== memberId),
            }
          : null,
      }))
    } catch (error) {
      this.setError(error)
      throw error
    }
  },

  async updateMemberRole(memberId: number, role: WorkspaceMemberRole): Promise<void> {
    try {
      await workspaceApi.updateMemberRole(memberId, { role })
      setWorkspaceState((prev) => ({
        ...prev,
        workspace: prev.workspace
          ? {
              ...prev.workspace,
              members: prev.workspace.members.map((m) =>
                m.hiring_manager_id === memberId ? { ...m, role } : m
              ),
            }
          : null,
      }))
    } catch (error) {
      this.setError(error)
      throw error
    }
  },

  async fetchUsage(): Promise<void> {
    const requestedSlug = getActiveWorkspaceSlug()
    try {
      const usage = await workspaceApi.getWorkspaceUsage(requestedSlug)
      if (!isActiveWorkspace(requestedSlug)) {
        return this.fetchUsage()
      }
      setWorkspaceState((prev) => ({ ...prev, usage, usageError: null }))
    } catch (error) {
      if (!isActiveWorkspace(requestedSlug)) {
        return this.fetchUsage()
      }
      console.error('Failed to fetch workspace usage:', error)
      setWorkspaceState((prev) => ({ ...prev, usageError: error }))
    }
  },

  async fetchInvitations(): Promise<void> {
    try {
      const invitations = await workspaceApi.fetchInvitations()
      setWorkspaceState((prev) => ({
        ...prev,
        invitations,
      }))
    } catch (error) {
      console.error('Failed to fetch invitations:', error)
    }
  },

  async inviteMember(data: InviteToWorkspaceRequest): Promise<void> {
    try {
      await workspaceApi.inviteMember(data)
      await this.fetchInvitations()
    } catch (error) {
      this.setError(error)
      throw error
    }
  },

  async cancelInvitation(invitationId: number): Promise<void> {
    try {
      await workspaceApi.cancelInvitation(invitationId)
      setWorkspaceState((prev) => ({
        ...prev,
        invitations: prev.invitations.filter((i) => i.id !== invitationId),
      }))
    } catch (error) {
      this.setError(error)
      throw error
    }
  },

  async acceptInvitation(token: string): Promise<void> {
    this.setLoading(true)
    try {
      const workspace = await workspaceApi.acceptInvitation(token)
      const workspaces = await workspaceApi.fetchWorkspaces()
      await authStore.revalidate()
      setWorkspaceState((prev) => ({
        ...prev,
        workspace,
        workspaces,
        invitations: [],
        usage: null,
        isLoading: false,
        error: null,
      }))
    } catch (error) {
      this.setError(error)
      throw error
    }
  },

  isFeatureEnabled(key: string): boolean {
    return workspaceState().workspace?.workspace_features?.[key] ?? false
  },

  clear() {
    setActiveWorkspaceSlug(null)
    clearBrandCssCache()
    setWorkspaceState({
      workspaces: [],
      workspace: null,
      invitations: [],
      usage: null,
      usageError: null,
      isLoading: false,
      error: null,
    })
  },
}
